import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard, StoryBadge } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

interface MobileStoryHeadlineCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  priority?: boolean;
}

/**
 * Headline-first card for BREAKING stories and story of the week.
 *
 * Layout: badge label at top → large centered headline → full-width image below.
 * Used for maximum editorial emphasis on the most important story.
 */
export function MobileStoryHeadlineCard({
  story,
  exploreBase,
  priority = false,
}: MobileStoryHeadlineCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const badgeLabel = deriveBadgeLabel({ story });
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group flex flex-col overflow-hidden rounded-2xl bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow-lg)] active:scale-[0.995]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Top section: badge + headline */}
      <div className="flex flex-col items-center px-5 pt-5 pb-3">
        {/* Badge label */}
        {badgeLabel && (
          <span
            className={`mb-3 inline-flex rounded-full px-3 py-1 font-bold text-[0.7rem] uppercase tracking-[0.06em] ${badgeStyleClass}`}
          >
            {badgeLabel}
          </span>
        )}

        {/* Large centered headline */}
        <h3
          className="line-clamp-4 text-pretty text-center font-bold text-[1.4rem] text-[var(--explore-text-primary)] leading-[1.25]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        {/* Summary — gives enough info-scent for the top story */}
        {story.summary && (
          <p className="mt-2 line-clamp-2 text-center text-[0.9rem] text-[var(--explore-text-secondary)] leading-[1.45]">
            {story.summary}
          </p>
        )}
      </div>

      {/* Full-width image */}
      <div className="relative mx-3 mb-3 aspect-[16/9] overflow-hidden rounded-xl bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt={story.headline}
          className="h-full w-full object-cover"
          fill
          priority={priority}
          sizes="(max-width: 640px) 100vw, 50vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />

        {story.isNew && (
          <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
            <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
          </div>
        )}
      </div>

      {/* Bottom meta: tag + time */}
      <div className="flex items-center justify-center gap-1.5 px-5 pb-4 text-[0.75rem] text-[var(--explore-text-muted)]">
        {primaryTag && (
          <>
            <span className="font-medium">{primaryTag.displayName}</span>
            <span>·</span>
          </>
        )}
        <span>{story.timeAgo}</span>
        <span>·</span>
        <span>{story.readTime}</span>
      </div>
    </Link>
  );
}

const BADGE_LABELS: Partial<Record<StoryBadge, string>> = {
  breaking: "Breaking",
  release: "Release",
  workflow: "Workflow",
  prompt: "Prompt",
};

function deriveBadgeLabel({
  story,
}: {
  story: ExploreStoryCard;
}): string | null {
  if (story.lifecycle === "BREAKING") {
    return "Breaking";
  }
  return BADGE_LABELS[story.storyBadge] ?? null;
}
